import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import './SshHostKeys.css';

import { HOST_KEY_MODAL_Z } from './SshHostKeyPromptModal';

/**
 * A saved server presented a DIFFERENT host key. The connection has already
 * been refused in Rust; this only explains why. There is deliberately no
 * "connect anyway" here: the way forward is removing the saved key in
 * Settings, a separate and deliberate step (`onOpenSettings`).
 */
export default function SshHostKeyChangedModal({ changed, onClose, onOpenSettings }) {
  const { t } = useTranslation();
  if (!changed) return null;
  const hostLabel = `${changed.host}:${changed.port}`;

  return (
    <Modal
      // Above every other dialog: a host-key question can come from any
      // operation — a git sync behind the open Connect dialog used to put it
      // UNDER that dialog, unclickable, until it timed out as refused (02.10.2026).
      zIndex={HOST_KEY_MODAL_Z}
      isOpen
      onClose={onClose}
      type="danger"
      title={t('sshHostKeys.changedTitle', 'Server identity changed')}
      subtitle={hostLabel}
    >
      <div className="space-y-4" data-testid="ssh-hostkey-changed">
        <p className="ssh-hostkey-warning text-gray-700 dark:text-zinc-200">
          {t(
            'sshHostKeys.changedBody',
            'The server sent a different host key than the one saved on this device, so the connection was stopped and no password or file was sent. Someone may be intercepting the connection, or the server was reinstalled.'
          )}
        </p>
        <dl className="ssh-hostkey-facts">
          <dt>{t('sshHostKeys.savedKey', 'Saved key')}</dt>
          <dd className="ssh-hostkey-fp ssh-hostkey-fp--old" data-testid="ssh-hostkey-old">
            {changed.savedKeyType} {changed.savedFingerprint}
          </dd>
          <dt>{t('sshHostKeys.newKey', 'Key sent now')}</dt>
          <dd className="ssh-hostkey-fp" data-testid="ssh-hostkey-new">
            {changed.keyType} {changed.fingerprint}
          </dd>
        </dl>
        <p className="ssh-hostkey-warning text-gray-600 dark:text-zinc-300">
          {t(
            'sshHostKeys.changedHint',
            'Only if you know the server was reinstalled: remove the saved key under Settings → Known hosts (Terminal or FTP), then connect again and check the new fingerprint.'
          )}
        </p>
        <div className="modal-footer-row">
          {onOpenSettings && (
            <button
              type="button"
              onClick={onOpenSettings}
              className="ssh-hostkey-btn px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
            >
              {t('sshHostKeys.openSettings', 'Open known hosts')}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="ssh-hostkey-btn px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm"
          >
            {t('sshHostKeys.close', 'Close')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
